import type { ReactElement } from 'react';
import type { StoryVersion } from '@/types/story';

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PageLayout from '@/components/PageLayout';
import VersionBadge from '@/components/VersionBadge';
import { Heart, Sparkles, Eye, User, BookOpen, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getBookTypeLabel } from '@/lib/utils';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

interface FavoriteStory {
  id: string;
  title: string;
  thumbnail?: string;
  age_range: string;
  page_count: number;
  version?: StoryVersion;
  is_pro_story?: boolean;
  author_name?: string;
  author_picture?: string;
  favorites_count?: number;
  views_count?: number;
  created_at: string;
}

// Helper to get full image URL (handles both local /static/ paths and external URLs)
const getImageUrl = (url: string | undefined | null): string | null => {
  if (!url) return null;
  if (url.startsWith('/static/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

interface FavoritesResponse {
  stories: FavoriteStory[];
  total: number;
  hasMore: boolean;
}

const FavoritesPage = (): ReactElement => {
  const navigate = useNavigate();
  const [stories, setStories] = useState<FavoriteStory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [total, setTotal] = useState<number>(0);
  const ITEMS_PER_PAGE = 25;

  useEffect(() => {
    loadFavorites(true);
  }, []);

  const loadFavorites = async (reset: boolean = false): Promise<void> => {
    if (!reset && (!hasMore || loadingMore)) return;

    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const offset = reset ? 0 : stories.length;
      const params = new URLSearchParams();
      params.append('limit', ITEMS_PER_PAGE.toString());
      params.append('offset', offset.toString());

      const response = await axios.get<FavoritesResponse>(`${API}/stories/favorites?${params}`, {
        withCredentials: true
      });

      if (reset) {
        setStories(response.data.stories);
      } else {
        setStories(prev => [...prev, ...response.data.stories]);
      }

      setTotal(response.data.total);
      setHasMore(response.data.hasMore);
    } catch (error) {
      console.error('Failed to load favorites:', error);
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        toast.error('Please login to view favorites');
        navigate('/login');
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Infinite scroll handler
  const handleLoadMore = useCallback(() => {
    loadFavorites(false);
  }, [stories.length, hasMore]);

  // Hook up infinite scroll - returns a ref to attach to loader element
  const loaderRef = useInfiniteScroll({
    onLoadMore: handleLoadMore,
    hasMore,
    loading: loading || loadingMore
  });

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <PageLayout>
      <div className="flex-1">
        <div className="max-w-screen-2xl mx-auto px-4 py-6 md:p-8 2xl:px-12">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
              Favorite Stories
            </h1>
            <p style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
              {total} {total === 1 ? 'story' : 'stories'} you love
            </p>
          </div>

          {/* Stories Grid */}
          {loading ? (
            <div className="text-center py-12">
              <Sparkles className="w-12 h-12 mx-auto mb-4 animate-spin" style={{ color: 'var(--st-primary)' }} />
              <p style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>Loading favorites...</p>
            </div>
          ) : stories.length === 0 ? (
            <div className="text-center py-12 rounded-2xl" style={{ background: 'var(--st-bg-surface)' }}>
              <Heart className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--st-border-medium)' }} />
              <p className="font-bold mb-2" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>No favorites yet</p>
              <p className="text-sm mb-4" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                Explore stories and save your favorites!
              </p>
              <button
                onClick={() => navigate('/explore')}
                className="px-6 py-2 rounded-full font-bold"
                style={{ background: 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)', color: 'white' }}
              >
                Explore Stories
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
                {stories.map((story) => (
                  <div
                    key={story.id}
                    data-testid={`favorite-${story.id}`}
                    className="rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer h-[340px] flex flex-col"
                    style={{ background: 'var(--st-bg-surface)' }}
                    onClick={() => navigate(`/story/${story.id}`)}
                  >
                    {/* Thumbnail */}
                    <div className="h-44 flex-shrink-0 overflow-hidden" style={{ background: 'linear-gradient(135deg, var(--st-border-light) 0%, var(--st-border-medium) 100%)' }}>
                      {story.thumbnail ? (
                        <img
                          src={getImageUrl(story.thumbnail) || undefined}
                          alt={story.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <BookOpen className="w-12 h-12" style={{ color: 'var(--st-primary-hover)' }} />
                        </div>
                      )}
                    </div>

                    <div className="p-4 flex-1 flex flex-col">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="px-2 py-1 rounded-full text-xs font-bold" style={{ background: '#E8E8E8', color: 'var(--st-text-heading)' }}>
                          {getBookTypeLabel(story.age_range)}
                        </span>
                        {story.page_count > 0 && (
                          <span className="px-2 py-1 rounded-full text-xs font-bold" style={{ background: '#E8F4F8', color: '#5B8A9A' }}>
                            {story.page_count}p
                          </span>
                        )}
                        <VersionBadge version={story.version} isProStory={story.is_pro_story} size="small" />
                      </div>

                      <h3 className="font-bold text-base mb-2 line-clamp-1 h-6" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                        {story.title}
                      </h3>

                      <div className="flex items-center gap-2 mb-3">
                        {story.author_picture ? (
                          <img src={getImageUrl(story.author_picture) || undefined} alt={story.author_name} className="w-5 h-5 rounded-full" />
                        ) : (
                          <User className="w-5 h-5 p-1 rounded-full" style={{ background: 'var(--st-border-light)', color: 'var(--st-primary)' }} />
                        )}
                        <span className="text-xs font-semibold" style={{ color: 'var(--st-text-muted)', fontFamily: 'Quicksand, sans-serif' }}>
                          {story.author_name || 'Anonymous'}
                        </span>
                      </div>

                      <div className="flex items-center justify-between text-xs mt-auto" style={{ color: 'var(--st-text-muted)', fontFamily: 'Quicksand, sans-serif' }}>
                        <div className="flex items-center gap-3">
                          {story.favorites_count && story.favorites_count > 0 && (
                            <span className="flex items-center gap-1">
                              <Heart className="w-3 h-3" fill="var(--st-primary)" stroke="var(--st-primary)" />
                              {story.favorites_count}
                            </span>
                          )}
                          {story.views_count && story.views_count > 0 && (
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {story.views_count}
                            </span>
                          )}
                        </div>
                        <span>{formatDate(story.created_at)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Infinite scroll trigger element */}
              {hasMore && (
                <div ref={loaderRef} className="flex justify-center py-8">
                  {loadingMore && (
                    <>
                      <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--st-primary)' }} />
                      <p className="text-sm ml-2" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                        Loading more favorites...
                      </p>
                    </>
                  )}
                </div>
              )}

              {/* End of Results */}
              {!hasMore && stories.length > 0 && (
                <div className="text-center py-8">
                  <p className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                    You've reached the end!
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default FavoritesPage;
